import { useState, useEffect } from "react";
import { X, Cookie, Settings } from "lucide-react";

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    functional: false,
    analytics: false,
    performance: false,
    advertisement: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem("isiap_cookie_consent");
    if (!cookieConsent) {
      // Show banner after a small delay
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      performance: true,
      advertisement: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("isiap_cookie_consent", JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const allRejected = {
      necessary: true,
      functional: false,
      analytics: false,
      performance: false,
      advertisement: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("isiap_cookie_consent", JSON.stringify(allRejected));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const savedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("isiap_cookie_consent", JSON.stringify(savedPreferences));
    setIsVisible(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (key) => {
    if (key === "necessary") return; // Can't disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-[9998]" />

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6" data-testid="cookie-banner">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-[#e2e4ed] overflow-hidden">
          {!showSettings ? (
            /* Main Banner */
            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
                  <Cookie size={24} className="text-[#FF6B00]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1a1f36] mb-2">
                    Teniamo alla tua privacy
                  </h3>
                  <p className="text-[#4a5068] text-sm leading-relaxed">
                    Utilizziamo i cookie per migliorare la tua esperienza di navigazione, 
                    offrire contenuti o annunci personalizzati e analizzare il traffico del sito. 
                    Cliccando su <strong>"Accetta tutto"</strong>, acconsenti all'utilizzo dei cookie.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 h-12 px-6 rounded-lg bg-[#FF6B00] text-white font-semibold hover:bg-[#E55F00] transition-colors"
                  data-testid="cookie-accept-all"
                >
                  Accetta tutto
                </button>
                <button
                  onClick={handleRejectAll}
                  className="flex-1 h-12 px-6 rounded-lg bg-[#f5f6fa] text-[#1a1f36] font-semibold hover:bg-[#e2e4ed] transition-colors"
                  data-testid="cookie-reject-all"
                >
                  Rifiuta tutto
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex-1 h-12 px-6 rounded-lg border-2 border-[#e2e4ed] text-[#1a1f36] font-semibold hover:bg-[#f5f6fa] transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-customize"
                >
                  <Settings size={18} />
                  Personalizza
                </button>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#1a1f36]">
                  Personalizza le preferenze
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 rounded-lg hover:bg-[#f5f6fa] transition-colors"
                >
                  <X size={20} className="text-[#4a5068]" />
                </button>
              </div>

              <p className="text-[#4a5068] text-sm mb-6">
                Qui sotto troverai informazioni dettagliate su tutti i cookie suddivisi per categoria di consenso.
              </p>

              <div className="space-y-4 mb-6 max-h-[300px] overflow-y-auto">
                {/* Necessary */}
                <div className="p-4 rounded-xl bg-[#f5f6fa] border border-[#e2e4ed]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-[#1a1f36]">Necessari</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-[#FF6B00] text-white">
                      Sempre attivi
                    </span>
                  </div>
                  <p className="text-sm text-[#4a5068]">
                    I cookie necessari sono fondamentali per permettere il funzionamento di base del sito. 
                    Non memorizzano dati personali identificabili.
                  </p>
                </div>

                {/* Functional */}
                <div className="p-4 rounded-xl border border-[#e2e4ed]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-[#1a1f36]">Funzionali</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={() => handlePreferenceChange("functional")}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-[#e2e4ed] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B00]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-[#4a5068]">
                    Aiutano a svolgere funzionalità come la condivisione sui social media e la raccolta di feedback.
                  </p>
                </div>

                {/* Analytics */}
                <div className="p-4 rounded-xl border border-[#e2e4ed]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-[#1a1f36]">Analitici</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange("analytics")}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-[#e2e4ed] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B00]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-[#4a5068]">
                    Utilizzati per capire come i visitatori interagiscono con il sito (numero visitatori, frequenza di rimbalzo, ecc.).
                  </p>
                </div>

                {/* Performance */}
                <div className="p-4 rounded-xl border border-[#e2e4ed]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-[#1a1f36]">Prestazioni</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.performance}
                        onChange={() => handlePreferenceChange("performance")}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-[#e2e4ed] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B00]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-[#4a5068]">
                    Utilizzati per analizzare gli indici di performance chiave del sito per una migliore esperienza utente.
                  </p>
                </div>

                {/* Advertisement */}
                <div className="p-4 rounded-xl border border-[#e2e4ed]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-[#1a1f36]">Pubblicità</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.advertisement}
                        onChange={() => handlePreferenceChange("advertisement")}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-[#e2e4ed] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6B00]"></div>
                    </label>
                  </div>
                  <p className="text-sm text-[#4a5068]">
                    Utilizzati per fornire pubblicità personalizzata in base alle pagine visitate e analizzare l'efficacia delle campagne.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 h-12 px-6 rounded-lg bg-[#f5f6fa] text-[#1a1f36] font-semibold hover:bg-[#e2e4ed] transition-colors"
                >
                  Rifiuta tutto
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 h-12 px-6 rounded-lg bg-[#FF6B00] text-white font-semibold hover:bg-[#E55F00] transition-colors"
                  data-testid="cookie-save-preferences"
                >
                  Salva le mie preferenze
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 h-12 px-6 rounded-lg border-2 border-[#FF6B00] text-[#FF6B00] font-semibold hover:bg-[#FF6B00]/10 transition-colors"
                >
                  Accetta tutto
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
